import { useEffect, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import type { PageSize } from "@/components/common/usePagination";
import { SectionCard } from "@/components/common/SectionCard";
import { ManufacturingGateBanner } from "@/components/manufacturing/ManufacturingGateBanner";
import { ghostBtnClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useManufacturingEntitlementGate } from "@/components/manufacturing/useManufacturingEntitlementGate";
import { COGS_PAGE_SIZES } from "@/lib/manufacturing/cogsPaging";
import { shelfVarianceRowKey } from "@/lib/manufacturing/cogsShelfVarianceReport";
import { downloadMissingMaterialsPdf } from "@/lib/manufacturing/missingMaterialsReportPdf";
import type { CogsRefreshScope } from "./useCogsPageMaterialsCalc";
import { ManufacturingPricingCalcClock } from "./ManufacturingPricingCalcClock";
import { ManufacturingPricingGridFilters } from "./ManufacturingPricingGridFilters";
import { ManufacturingPricingSkuScope } from "./ManufacturingPricingSkuScope";
import { ManufacturingPricingGridTable } from "./ManufacturingPricingGridTable";
import { ManufacturingPricingPageCalcDialog } from "./ManufacturingPricingPageCalcDialog";
import { ManufacturingPricingToolsCard } from "./ManufacturingPricingToolsCard";
import { PricingShelfVarianceDrill } from "./PricingShelfVarianceDrill";
import { PricingShelfVarianceReport } from "./PricingShelfVarianceReport";
import { useManufacturingPricingGrid } from "./useManufacturingPricingGrid";

export function ManufacturingPricingGridPanel() {
  const gate = useManufacturingEntitlementGate();
  const [navTick, setNavTick] = useState(0);
  const [calcMinimized, setCalcMinimized] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [drillKey, setDrillKey] = useState<string | null>(null);
  const grid = useManufacturingPricingGrid(gate.enabled, navTick);
  const loadingPage = grid.listBusy && grid.rows.length === 0;
  const calcOpen = Boolean(grid.pageCalc) || loadingPage;
  const drillRow = drillKey ? grid.filteredRows.find((row) => shelfVarianceRowKey(row) === drillKey) ?? null : null;

  useEffect(() => {
    if (!calcOpen) setCalcMinimized(false);
  }, [calcOpen]);

  return (
    <div className="w-full text-left space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">COGS</h2>
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            Materials → COGS → Wholesale → MSRP. Org-wide base, or pick a plant for overrides.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => void grid.reloadGrid()}
            disabled={!gate.enabled || gate.loading || grid.listBusy}
          >
            {grid.listBusy ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            onClick={() => {
              gate.refresh();
              setNavTick((s) => s + 1);
            }}
            disabled={gate.loading || grid.listBusy}
          >
            Reload
          </button>
        </div>
      </div>

      <ManufacturingGateBanner gate={gate} />

      {gate.enabled && grid.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {grid.localError}
        </div>
      ) : null}

      {gate.enabled ? (
        <>
          <SectionCard title="Filters" description="Product SKUs · plant scope overlays org base.">
            <ManufacturingPricingGridFilters grid={grid} />
          </SectionCard>

          <ManufacturingPricingToolsCard grid={grid} />

          <SectionCard
            title="SKU COGS"
            description="Saved materials totals. Group, style, size, and pages use the catalog loaded in this browser. Open a missing-cost note to see which parts still have no unit cost."
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <ManufacturingPricingSkuScope
                  value={grid.filters.sku_scope}
                  disabled={grid.listBusy}
                  onChange={(sku_scope) => grid.setFilters((prev) => ({ ...prev, sku_scope }))}
                />
                <button
                  type="button"
                  className={`${ghostBtnClass}${
                    grid.pageCalc
                      ? " !bg-gray-100 !text-gray-400 dark:!bg-zinc-800 dark:!text-zinc-500"
                      : ""
                  }`}
                  onClick={() => void grid.refreshMaterials("page")}
                  disabled={
                    grid.actionBusy || grid.listBusy || Boolean(grid.pageCalc) || grid.rows.length === 0
                  }
                >
                  Refresh materials (page)
                </button>
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={grid.listBusy || grid.filteredRows.length === 0}
                  onClick={() => {
                    const plant = grid.plants.find((item) => item.id === grid.filters.plant_id);
                    downloadMissingMaterialsPdf({
                      scope: plant?.name || "Org-wide",
                      generatedOn: new Date().toISOString().slice(0, 10),
                      rows: grid.filteredRows,
                    });
                  }}
                >
                  Missing materials PDF
                </button>
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={grid.listBusy || grid.filteredRows.length === 0}
                  aria-pressed={reportOpen}
                  onClick={() => setReportOpen((open) => !open)}
                >
                  Variance report
                </button>
                <select
                  className={`${ghostBtnClass} max-w-[11rem]`}
                  aria-label="Extend materials refresh"
                  value=""
                  disabled={
                    grid.actionBusy || grid.listBusy || Boolean(grid.pageCalc) || grid.totalCount === 0
                  }
                  onChange={(event) => {
                    const scope = event.target.value as CogsRefreshScope;
                    if (scope === "later" || scope === "all") void grid.refreshMaterials(scope);
                  }}
                >
                  <option value="">Also refresh…</option>
                  <option value="later">This page and later pages</option>
                  <option value="all">All loaded pages</option>
                </select>
                {calcMinimized && calcOpen ? (
                  <ManufacturingPricingCalcClock
                    pageCalc={grid.pageCalc}
                    loading={loadingPage}
                    backgroundFill={grid.backgroundFill}
                    onExpand={() => setCalcMinimized(false)}
                    onContinue={() => {
                      grid.continueBackgroundFill();
                      setCalcMinimized(true);
                    }}
                  />
                ) : null}
              </div>
              <PaginationBar
                pageIndex={grid.pageIndex}
                pageCount={grid.pageCount}
                pageSize={grid.pageSize as PageSize}
                totalCount={grid.totalCount}
                firstShown={grid.pageStart}
                lastShown={grid.pageEnd}
                onPageChange={grid.goToPage}
                onPageSizeChange={grid.setPageSize}
                pageSizeOptions={COGS_PAGE_SIZES}
                noun={
                  grid.filters.sku_scope === "addons"
                    ? "options"
                    : grid.filters.sku_scope === "all"
                      ? "SKUs"
                      : "products"
                }
                alwaysShow
              />
            </div>
            {drillKey ? (
              drillRow ? (
                <PricingShelfVarianceDrill row={drillRow} columns={grid.columns} onClose={() => setDrillKey(null)} />
              ) : (
                <p className="mb-3 text-sm text-gray-500 dark:text-zinc-400">
                  That product is outside the current filters.
                </p>
              )
            ) : null}
            {reportOpen ? (
              <PricingShelfVarianceReport
                rows={grid.filteredRows}
                selectedKey={drillKey}
                onSelect={setDrillKey}
              />
            ) : null}
            <ManufacturingPricingGridTable
              grid={grid}
              onOpenVariance={(row) => setDrillKey(shelfVarianceRowKey(row))}
            />
          </SectionCard>
          {calcOpen && !calcMinimized ? (
            grid.pageCalc ? (
              <ManufacturingPricingPageCalcDialog
                mode={grid.pageCalc.phase}
                done={grid.pageCalc.done}
                total={grid.pageCalc.total}
                background={grid.pageCalc.background}
                onMinimize={() => setCalcMinimized(true)}
              />
            ) : (
              <ManufacturingPricingPageCalcDialog
                mode="loading"
                done={0}
                total={grid.pageSize}
                onMinimize={() => setCalcMinimized(true)}
              />
            )
          ) : null}
        </>
      ) : null}
    </div>
  );
}
